import clsx from "clsx";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import TimeService from "src/services/timeService/timeService";
import { DateField } from "src/ui/shared/form/DateField";
import { makeStyles } from "tss-react/mui";

import {
    Box,
    BoxProps,
    IconButton,
    Paper,
    TableContainer as MuiTableContainer,
    TextFieldProps,
    Theme,
    Typography,
    useTheme,
} from "@mui/material";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { UncontrolledTextField } from "@shared/form/TextField";
import { LoadingIcon } from "@shared/Loading";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTableContainer } from "@shared/table/MinimalTableComponents";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        flexDirection: "column",
        position: "relative",
        height: "100%",
        // `flex: 1` + `minHeight: 0` so the container resolves against a filling PaneTabPanel and
        // lets a TableLayout child shrink instead of overflowing it.
        flex: 1,
        minHeight: 0,
        display: "flex",
        padding: theme.spacing(3),
    },
    loadingContainer: {
        minHeight: 120,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
    },
    noDataContainer: {
        minHeight: 150,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    section: {
        display: "flex",
        flexDirection: "column",
        borderBottomStyle: "solid",
        borderBottomColor: "lightGray",
        borderBottomWidth: 1,
        marginBottom: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    sectionWithoutBorder: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        marginBottom: theme.spacing(5),
    },
    tableContainer: { maxHeight: "100%", marginBottom: theme.spacing(15) },
    headerBox: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(4),
        backgroundColor: theme.palette.emteria.background.tableHeaders,
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
    },
    cancelButton: {
        backgroundColor: theme.palette.emteria.background.paper,
        padding: theme.spacing(1.3),
        "&:hover": {
            backgroundColor: theme.palette.emteria.background.paperDeep,
        },
    },
    saveButton: {
        backgroundColor: theme.palette.emteria.background.paper,
        padding: theme.spacing(1.3),
        "&:hover": {
            backgroundColor: theme.palette.emteria.background.paperDeep,
        },
        "&:disabled": {
            backgroundColor: theme.palette.emteria.background.paper,
        },
    },
    inputContainer: {
        width: "100%",
        minWidth: 0,
        marginTop: theme.spacing(0.5),
    },
    editButton: {
        backgroundColor: theme.palette.emteria.background.paper,
        padding: theme.spacing(1.3),
        "&:hover": {
            backgroundColor: theme.palette.emteria.background.paperDeep,
        },
    },
    editableLabelWrapper: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        width: "100%",
        minWidth: 0,
    },
    editableLabel: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        flex: "0 1 auto",
    },
    editableButtonWrapper: {
        flexShrink: 0,
    },
}));

const Container = (props: React.PropsWithChildren<{ className?: string }>) => {
    const { classes } = useStyles();
    return <Box className={clsx(classes.container, props.className)}>{props.children}</Box>;
};
const TableContainer = (props: React.PropsWithChildren<object> & { style?: React.CSSProperties }) => {
    const { classes } = useStyles();
    return (
        <MuiTableContainer component={Paper} className={classes.tableContainer} style={props.style}>
            {props.children}
        </MuiTableContainer>
    );
};

type TableLayoutProps = React.PropsWithChildren<{
    /** Re-measure the scrollport when these change — page index/size, row count, ... */
    contentDeps?: React.DependencyList;
    pagination?: React.ReactNode;
}>;

/**
 * Table layout for a side-panel tab whose main content is a table, for use inside a
 * `PaneTabPanel fill`. Deliberately built on the very same classes as the full-page tables
 * (`useFullPageTableLayoutStyles`) so the Paper border and elevation shadow cannot drift apart
 * between a page and a panel: the table fills the available height, scrolls internally only once
 * the rows overflow it, and the pagination footer stays pinned at a constant height.
 */
const TableLayout = ({ children, contentDeps = [], pagination }: TableLayoutProps) => {
    const { classes, cx } = useFullPageTableLayoutStyles();
    const { ref, contentOverflows } = useContainerOverflow(true, contentDeps);

    return (
        <Box className={classes.tableLayout}>
            <Box className={classes.tableWrapper}>
                <MinimalTableContainer
                    ref={ref}
                    className={cx(classes.tableContainer, !contentOverflows && classes.tableContainerNoScroll)}
                >
                    {children}
                </MinimalTableContainer>
            </Box>
            {pagination ? <Box className={classes.pagination}>{pagination}</Box> : null}
        </Box>
    );
};
const Section = ({ withoutBorder, ...props }: React.PropsWithChildren<BoxProps & { withoutBorder?: boolean }>) => {
    const { classes } = useStyles();
    return (
        <Box className={withoutBorder ? classes.sectionWithoutBorder : classes.section} {...props}>
            {props.children}
        </Box>
    );
};
const Loader = () => {
    const { classes } = useStyles();
    return (
        <Box className={classes.loadingContainer}>
            <LoadingIcon />
        </Box>
    );
};
const Header = (props: React.PropsWithChildren<object>) => {
    const theme = useTheme();
    return (
        <Typography
            variant="subtitle1"
            sx={{
                fontWeight: 600,
                display: "block",
                marginBottom: theme.spacing(3),
            }}
        >
            {props.children}
        </Typography>
    );
};
const Text = (props: React.PropsWithChildren<object>) => {
    return <Typography variant="body1">{props.children}</Typography>;
};
const NoData = (props: { message: string }) => {
    const { classes } = useStyles();
    return (
        <Box className={classes.noDataContainer}>
            <Text>{props.message}</Text>
        </Box>
    );
};

const HeaderBox = (props: React.PropsWithChildren<{ className?: string }>) => {
    const { classes } = useStyles();
    return <Box className={clsx(classes.headerBox, props.className)}>{props.children}</Box>;
};

type EditableInputLayoutWrapperProps = {
    editing: boolean;
    label: string | number;
    saving: boolean;
    saveDisabled?: boolean;
    disabledTooltip?: string;
    onCancel: () => void;
    onEdit: () => void;
    onSave: () => Promise<void>;
};
const EditableInputLayoutWrapper = (props: React.PropsWithChildren<EditableInputLayoutWrapperProps>) => {
    const { classes } = useStyles();
    const theme = useTheme();
    const { t } = useTranslation(["common"]);

    if (props.editing) {
        return (
            <Box display="flex" alignItems="center" gap={1} width="100%">
                <Box className={classes.inputContainer}>{props.children}</Box>
                {props.saving ? (
                    <Box sx={{ display: "flex", alignItems: "center", padding: theme.spacing(1.3) }}>
                        <LoadingIcon />
                    </Box>
                ) : (
                    <React.Fragment>
                        <Tooltip title={t("common:button.cancel")}>
                            <IconButton size="medium" onClick={props.onCancel} className={classes.cancelButton}>
                                <i className="fa-solid fa-fw fa-xmark" style={{ fontSize: "16px" }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common:button.save")}>
                            <IconButton
                                size="medium"
                                onClick={props.onSave}
                                color="primary"
                                className={classes.saveButton}
                                disabled={props.saveDisabled}
                            >
                                <i className="fa-solid fa-fw fa-check" style={{ fontSize: "16px" }} />
                            </IconButton>
                        </Tooltip>
                    </React.Fragment>
                )}
            </Box>
        );
    }

    return (
        <Box className={classes.editableLabelWrapper}>
            <Tooltip title={props.label} disableChildrenWrapper>
                <Box component="span" className={classes.editableLabel}>
                    {props.label}
                </Box>
            </Tooltip>
            <Box component="span" className={classes.editableButtonWrapper}>
                <Tooltip title={props.disabledTooltip ?? t("common:button.edit")}>
                    <span>
                        <IconButton
                            size="medium"
                            onClick={props.onEdit}
                            className={classes.editButton}
                            disabled={!!props.disabledTooltip}
                        >
                            <i className="fa-solid fa-fw fa-pencil" style={{ fontSize: "16px" }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};

type EditableInputProps<T> = {
    initialValue: T;
    onSave: (val: T) => Promise<void>;
    disabledTooltip?: string;
    multiline?: boolean;
    minRows?: number;
    maxLength?: number;
};
const EditableInput = <T extends string | number>(props: EditableInputProps<T>) => {
    const { t } = useTranslation(["common"]);
    const [value, setValue] = React.useState<T>(props.initialValue);
    const [saving, setSaving] = React.useState(false);
    const [editing, setEditing] = React.useState(false);

    const isTooLong = props.maxLength !== undefined && String(value).length > props.maxLength;

    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

    const handleInputRef = (element: HTMLInputElement | HTMLTextAreaElement | null) => {
        inputRef.current = element;
    };

    const onEdit = () => {
        setEditing(true);
    };
    const onSave = async () => {
        if (isTooLong) {
            return;
        }

        try {
            setSaving(true);
            await props.onSave(value);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setSaving(false);
            setEditing(false);
        }
    };
    const onCancel = () => {
        setValue(props.initialValue);
        setEditing(false);
    };
    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey && !saving && !props.multiline) {
            event.preventDefault();
            event.stopPropagation();
            onSave();
        } else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            onCancel();
        }
    };
    const onChange: TextFieldProps["onChange"] = (event) => {
        if (typeof event.target.value === "string" || typeof event.target.value === "number") {
            setValue(event.target.value as T);
        }
    };

    React.useEffect(() => {
        if (editing) {
            const timer = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [editing]);

    return (
        <EditableInputLayoutWrapper
            label={value || "-"}
            editing={editing}
            saving={saving}
            saveDisabled={isTooLong}
            onCancel={onCancel}
            onSave={onSave}
            onEdit={onEdit}
            disabledTooltip={props.disabledTooltip}
        >
            <UncontrolledTextField
                value={value}
                onChange={onChange}
                onKeyDown={onKeyDown}
                ref={handleInputRef}
                size="small"
                fullWidth
                multiline={props.multiline}
                minRows={props.multiline ? props.minRows : undefined}
                errorMessage={isTooLong ? t("common:validation.maxLength", { max: props.maxLength }) : undefined}
            />
        </EditableInputLayoutWrapper>
    );
};

type EditableDateInputProps = {
    initialValue: string | null;
    placeholder?: string;
    disabledTooltip?: string;
    onSave: (props: { date: Date | null; dateString: string | null }) => Promise<void>;
};
const EditableDateInput = (props: EditableDateInputProps) => {
    const initialDate = props.initialValue ? new Date(props.initialValue) : null;

    const [value, setValue] = React.useState<Date | null>(initialDate);
    const [saving, setSaving] = React.useState(false);
    const [editing, setEditing] = React.useState(false);

    const valueString = value ? TimeService.getFormattedDate(value, "date") : undefined;

    const onEdit = () => {
        setEditing(true);
    };
    const onSave = async () => {
        try {
            setSaving(true);
            await props.onSave({ date: value, dateString: valueString || null });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setSaving(false);
            setEditing(false);
        }
    };
    const onCancel = () => {
        setValue(initialDate);
        setEditing(false);
    };

    return (
        <EditableInputLayoutWrapper
            label={valueString || "-"}
            editing={editing}
            saving={saving}
            onCancel={onCancel}
            onSave={onSave}
            onEdit={onEdit}
            disabledTooltip={props.disabledTooltip}
        >
            <DateField selected={value} onChange={setValue} placeholder={props.placeholder} />
        </EditableInputLayoutWrapper>
    );
};

type EditableSelectInputProps = {
    initialValue: string | number | null;
    label: string;
    disabledTooltip?: string;
    options: { label: string; value: string | number }[];
    onSave: (val: string | number | null) => Promise<void>;
    disableClearable?: boolean;
};
const EditableSelectInput = (props: EditableSelectInputProps) => {
    const [value, setValue] = React.useState<string | number | null>(props.initialValue);
    const [saving, setSaving] = React.useState(false);
    const [editing, setEditing] = React.useState(false);

    const onEdit = () => {
        setEditing(true);
    };
    const onSave = async () => {
        try {
            setSaving(true);
            await props.onSave(value);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setSaving(false);
            setEditing(false);
        }
    };
    const onCancel = () => {
        setValue(props.initialValue);
        setEditing(false);
    };
    const handleChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (selectedOption && typeof selectedOption.value !== "string" && typeof selectedOption.value !== "number") {
            return;
        }

        setValue(selectedOption?.value ?? null);
    };

    return (
        <EditableInputLayoutWrapper
            label={props.label}
            editing={editing}
            saving={saving}
            onCancel={onCancel}
            onSave={onSave}
            onEdit={onEdit}
            disabledTooltip={props.disabledTooltip}
        >
            <SelectField
                options={props.options}
                value={props.options.find((option) => option.value === value)}
                onChange={handleChange}
                fullWidth
                disableClearable={!!props.disableClearable}
            />
        </EditableInputLayoutWrapper>
    );
};

type CopyableValueProps = {
    value: string | null | undefined;
    copyTooltip?: string;
};
const CopyableValue = (props: CopyableValueProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);

    const onCopy = () => {
        if (!props.value) {
            return;
        }
        navigator.clipboard
            .writeText(props.value)
            .then(() => showSuccessToast(t("common:toast.copiedToClipboardSuccess")))
            .catch(() => showErrorToast(t("common:toast.copiedToClipboardError")));
    };

    return (
        <Box className={classes.editableLabelWrapper}>
            <Tooltip title={props.value || "-"} disableChildrenWrapper>
                <Box component="span" className={classes.editableLabel}>
                    {props.value || "-"}
                </Box>
            </Tooltip>
            {props.value ? (
                <Box component="span" className={classes.editableButtonWrapper}>
                    <Tooltip title={props.copyTooltip ?? t("common:button.copy")}>
                        <IconButton size="medium" onClick={onCopy} className={classes.editButton}>
                            <i className="fa-solid fa-fw fa-copy" style={{ fontSize: "16px" }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : null}
        </Box>
    );
};

export const PaneElement = {
    TableContainer,
    TableLayout,
    Container,
    Section,
    Loader,
    NoData,
    Header,
    HeaderBox,
    Text,
    EditableDateInput,
    EditableInput,
    EditableSelectInput,
    CopyableValue,
};
